import { useMemo } from 'react'
import { MultiSelect, type SelectOption } from '@/components/ui/Select'
import { useStore } from '@/stores'
import { useTaxonomyStore } from '@/stores/taxonomyStore'
import type { KiqId } from '@/types'

export function KiqMultiSelect() {
  const selectedAssetId = useStore((state) => state.selectedAssetId)
  const selectedIndicationId = useStore((state) => state.selectedIndicationId)
  const selectedKiqIds = useStore((state) => state.selectedKiqIds)
  const setKiqIds = useStore((state) => state.setKiqIds)
  const { kiqs, status } = useTaxonomyStore((state) => ({
    kiqs: state.data
      ? state.data.keyInsightQuestions
          .filter((kiq) => kiq.indicationId === selectedIndicationId)
          .sort((a, b) => a.order - b.order)
      : [],
    status: state.status,
  }))

  const options = useMemo<SelectOption<KiqId>[]>(
    () => kiqs.map((kiq) => ({ id: kiq.id, label: kiq.prompt })),
    [kiqs],
  )

  if (options.length === 0) {
    if (!selectedIndicationId) {
      // Reveal hints one at a time: until an asset is chosen, IndicationSelect
      // already shows "Select an asset…", so this step stays hidden.
      if (!selectedAssetId) {
        return null
      }
      return (
        <p className="text-xs text-gray-400">
          Select an indication to view related Key Insight Topics.
        </p>
      )
    }
    if (status === 'error') {
      return <p className="text-xs text-red-400">Unable to load questions. Check your connection.</p>
    }
    return (
      <p className="text-xs text-gray-400">
        {status === 'loading' ? 'Loading questions…' : 'No questions available.'}
      </p>
    )
  }

  const triggerLabel =
    selectedKiqIds.length > 0 ? `${selectedKiqIds.length} selected` : 'Key Insight Topics'

  return (
    <MultiSelect<KiqId>
      value={selectedKiqIds}
      onChange={setKiqIds}
      options={options}
      triggerLabel={triggerLabel}
      ariaLabel="Select key insight questions"
      className="kiq-select"
    />
  )
}
